import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { ProjectApiTypes } from '@highlight-cp/project-api';
import type { SurveyConfig } from '@highlight-cp/survey-api';

export interface ResultsSlice {
  selectedResponseId: string;
  selectedResponseSurveyConfigId: string;
  siblingResponses: string[];
  selectedRow: NonNullable<ProjectApiTypes.QualityControlReport['statuses']>[number] | null;
  selectedSurvey: SurveyConfig | null;
  aggregationMode: Record<string, string>;
  isEditModeScorecard: boolean;
}

const initialState: ResultsSlice = {
  selectedResponseId: '',
  selectedResponseSurveyConfigId: '',
  siblingResponses: [],
  selectedRow: null,
  selectedSurvey: null,
  aggregationMode: {},
  isEditModeScorecard: false,
};

export const resultsSlice = createSlice({
  name: 'results',
  initialState,
  reducers: {
    closeDrawer: (state) => {
      state.selectedResponseId = initialState.selectedResponseId;
      state.siblingResponses = initialState.siblingResponses;
      state.selectedRow = initialState.selectedRow;
    },

    openDrawer: (
      state,
      action: PayloadAction<{
        id: string;
        row: ResultsSlice['selectedRow'];
        surveyConfigId?: string | null;
      }>,
    ) => {
      state.selectedResponseId = action.payload.id;
      state.selectedRow = action.payload.row;
      state.selectedResponseSurveyConfigId = action.payload.surveyConfigId ?? '';

      const siblings =
        Object.values(action.payload.row?.surveyReviews ?? {})
          ?.map((sr) => sr.surveyResponseId ?? '')
          ?.filter((id) => id !== action.payload.id && !!id) ?? [];

      if (siblings.length) {
        state.siblingResponses = siblings;
      }
    },

    closeSurveyDrawer: (state) => {
      state.selectedSurvey = initialState.selectedSurvey;
    },

    openSurveyDrawer: (
      state,
      action: PayloadAction<{ survey: ResultsSlice['selectedSurvey'] }>,
    ) => {
      state.selectedSurvey = action.payload.survey;
    },

    setAggregationMode: (
      state,
      action: PayloadAction<{ entryKey: string; aggregationType: string }>,
    ) => {
      state.aggregationMode[action.payload.entryKey] = action.payload.aggregationType;
    },

    updateAggregationMode: (state, action: PayloadAction<Record<string, string>>) => {
      state.aggregationMode = { ...state.aggregationMode, ...action.payload };
    },

    resetAggregationMode: (state) => {
      state.aggregationMode = initialState.aggregationMode;
    },

    setScorecardEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEditModeScorecard = action.payload;
    },
  },
});

export const {
  closeDrawer,
  openDrawer,
  closeSurveyDrawer,
  openSurveyDrawer,
  setAggregationMode,
  updateAggregationMode,
  resetAggregationMode,
  setScorecardEditMode,
} = resultsSlice.actions;
